<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import ShareSurveyUserCard from '@/components/share-survey/share-survey-user-card.vue';
import { ShareSurveyUser, ShareSurveyUserOption } from '@/types';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';

interface Props {
  options: ShareSurveyUserOption[];
  allUsersCount: number;
  isAllUsersAccessible: boolean;
  isLoading: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  select: [user: ShareSurveyUser];
  selectAllUsersOption: [];
}>();

const isFocused = ref(false);

const searchWord = ref<string>('');

const filteredOptions = computed(() => {
  return props.options.filter(
    option =>
      option.user_name.toLowerCase().includes(searchWord.value.toLowerCase()) ||
      option.email?.toLowerCase().includes(searchWord.value.toLowerCase()),
  );
});

const selectableAllUsersOption = computed(() => {
  return props.options.every(option => option.is_selectable);
});

const selectOption = (option: ShareSurveyUserOption) => {
  if (!option.is_selectable) return;
  isFocused.value = false;
  emit('select', {
    id: option.id,
    user_name: option.user_name,
    image_url: option.image_url,
    email: option.email,
  });
};

const selectAllUsersOption = () => {
  if (!selectableAllUsersOption.value) return;
  isFocused.value = false;
  emit('selectAllUsersOption');
};

const isClickedOutsideOptions = useOutsideClick([
  '#search-form-suggestion',
  '#search-bar',
]);

watch(isClickedOutsideOptions, isOutside => {
  if (isOutside && isFocused.value) isFocused.value = false;
  isClickedOutsideOptions.value = false;
});
</script>
<template>
  <div class="search-form">
    <SearchBar
      id="search-bar"
      class="search-bar"
      placeholder="ユーザーを検索"
      v-model="searchWord"
      @on-focus="isFocused = true"
    />
    <div
      class="search-form-suggestion c-text-regular--m"
      v-if="isFocused"
      id="search-form-suggestion"
    >
      <div v-if="isLoading" class="loading">
        <DgrLoading />
      </div>
      <template v-else-if="filteredOptions.length > 0 || searchWord === ''">
        <!-- すべてのユーザー -->
        <template v-if="!isAllUsersAccessible">
          <div
            class="all-users-option"
            @click="selectAllUsersOption"
            v-if="selectableAllUsersOption"
          >
            <DgrIcon name="building" />
            <div>環境内のすべてのユーザー（{{ allUsersCount }}人）</div>
          </div>
          <VTooltip placement="top" v-else>
            <div class="all-users-option disabled">
              <DgrIcon name="building" />
              <div>環境内のすべてのユーザー（{{ allUsersCount }}人）</div>
            </div>
            <template #popper>
              このシェアスレッドにアクセス権の無い社内情報が含まれています
            </template>
          </VTooltip>
        </template>
        <!-- 個別ユーザー -->
        <div v-for="option in filteredOptions" :key="option.id">
          <ShareSurveyUserCard
            v-if="option.is_selectable"
            @click="selectOption(option)"
            :selectable="option.is_selectable"
            :disabled="!option.is_selectable"
            :image-url="option.image_url || ''"
            :user-name="option.user_name"
            :email="option.email || ''"
          />
          <VTooltip placement="top" v-else>
            <ShareSurveyUserCard
              :selectable="option.is_selectable"
              :disabled="!option.is_selectable"
              :image-url="option.image_url || ''"
              :user-name="option.user_name"
              :email="option.email || ''"
            />
            <template #popper>
              このシェアスレッドにアクセス権の無い社内情報が含まれています
            </template>
          </VTooltip>
        </div>
      </template>
      <template v-else>
        <span>{{ searchWord }}</span>
        に一致する検索結果は見つかりませんでした。
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.search-form {
  position: relative;
}
.search-form-suggestion {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid $color-gray400;
  max-height: 264px;
  overflow: scroll;
  position: absolute;
  background-color: $color-white;
  top: 40px;
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  .all-users-option {
    display: flex;
    gap: 10px;
    padding: 8px;
    cursor: pointer;
    &:hover {
      background-color: $color-gray200;
    }
    &.disabled {
      cursor: not-allowed;
      background-color: $color-gray400;
    }
  }
}
</style>
